import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, Heart, MessageCircle, Flag, ShieldCheck } from "lucide-react";

export default function CommunityGuidelinesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[var(--liturgical-primary)] hover:underline mb-8 font-['Nunito']">
          <ArrowLeft className="w-4 h-4" /> Back to Parish
        </Link>
        
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E0D5] p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-8 h-8 text-[var(--liturgical-primary)]" />
            <h1 className="font-['Crimson_Text'] text-3xl md:text-4xl font-bold text-[#2D2A26]">Community Guidelines</h1>
          </div>
          
          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-8">Last Updated: February 25, 2026</p>
          
          <div className="bg-[#F9F7F2] rounded-lg p-6 mb-8">
            <p className="text-[#4A4A4A] font-['Nunito'] leading-relaxed italic">
              "A new command I give you: Love one another. As I have loved you, so you must love one another." — John 13:34
            </p>
          </div>
          
          <div className="prose prose-slate max-w-none font-['Nunito'] text-[#2D2A26]">
            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">Our Community Vision</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                Parish is a digital extension of the Catholic community—a place where the faithful can connect, support one another, and live out the Gospel in their daily lives. Our guidelines are rooted in Catholic social teaching and the call to treat every person with dignity and respect as a child of God.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[var(--liturgical-primary)]" /> Core Principles
              </h2>
              
              <div className="grid gap-4 mt-4">
                <div className="bg-[#F9F7F2] rounded-lg p-4">
                  <h3 className="font-semibold text-[#2D2A26] mb-2">1. Respect Human Dignity</h3>
                  <p className="text-[#4A4A4A] text-sm">
                    Every person is created in the image and likeness of God. Treat all members with respect, regardless of their background, opinions, or circumstances. Personal attacks, insults, and demeaning language are never acceptable.
                  </p>
                </div>
                
                <div className="bg-[#F9F7F2] rounded-lg p-4">
                  <h3 className="font-semibold text-[#2D2A26] mb-2">2. Practice Charity</h3>
                  <p className="text-[#4A4A4A] text-sm">
                    Approach every interaction with love and kindness. Before posting or commenting, ask yourself: "Is this charitable? Does this build up my neighbor?" Assume good intentions from others and give the benefit of the doubt.
                  </p>
                </div>
                
                <div className="bg-[#F9F7F2] rounded-lg p-4">
                  <h3 className="font-semibold text-[#2D2A26] mb-2">3. Speak Truth with Love</h3>
                  <p className="text-[#4A4A4A] text-sm">
                    Share accurate information and honest opinions, but always in a spirit of gentleness. Correction should be done privately when possible, and publicly only when necessary and with compassion.
                  </p>
                </div>
                
                <div className="bg-[#F9F7F2] rounded-lg p-4">
                  <h3 className="font-semibold text-[#2D2A26] mb-2">4. Build Community</h3>
                  <p className="text-[#4A4A4A] text-sm">
                    Parish exists to strengthen the bonds between Catholics. Contribute positively by sharing encouragement, celebrating others' joys, supporting those in difficulty, and fostering unity.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[var(--liturgical-primary)]" /> Content Standards
              </h2>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">Encouraged Content</h3>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Faith sharing, prayer requests, and spiritual encouragement</li>
                <li>Parish announcements, events, and community news</li>
                <li>Questions about faith, sacraments, and Catholic teaching</li>
                <li>Celebration of milestones (baptisms, confirmations, weddings, anniversaries)</li>
                <li>Charitable causes and volunteer opportunities</li>
                <li>Honest marketplace listings and fair business practices</li>
                <li>Constructive discussions that respect diverse perspectives within Catholic teaching</li>
              </ul>

              <h3 className="font-semibold text-[#2D2A26] mt-6 mb-2">Prohibited Content</h3>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Harassment & Bullying:</strong> Targeting individuals with hostile, threatening, or intimidating behavior</li>
                <li><strong>Hate Speech:</strong> Content that attacks or demeans people based on race, ethnicity, nationality, gender, disability, or other characteristics</li>
                <li><strong>Anti-Catholic Content:</strong> Material that attacks, mocks, or disrespects the Catholic faith, its teachings, or sacred traditions</li>
                <li><strong>Inappropriate Content:</strong> Pornography, sexually explicit material, excessive violence, or graphic content</li>
                <li><strong>Misinformation:</strong> Deliberately false or misleading information, especially regarding Church teaching or sacraments</li>
                <li><strong>Spam & Scams:</strong> Unsolicited promotions, pyramid schemes, fraudulent offers, or deceptive practices</li>
                <li><strong>Illegal Activity:</strong> Content promoting or facilitating illegal activities</li>
                <li><strong>Privacy Violations:</strong> Sharing others' personal information without consent</li>
                <li><strong>Divisive Politics:</strong> Partisan political content that creates division rather than unity (while respectful civic discussion is allowed)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[var(--liturgical-primary)]" /> Discussion Guidelines
              </h2>
              
              <div className="space-y-4 text-[#4A4A4A]">
                <p><strong>Stay On Topic:</strong> Keep discussions relevant to the post or community context.</p>
                
                <p><strong>Disagree Respectfully:</strong> It's okay to disagree, but do so with charity. Attack ideas, not people. Avoid personal insults, name-calling, or condescension.</p>
                
                <p><strong>Welcome Newcomers:</strong> Remember that new members may still be learning about the faith. Be patient and welcoming, not dismissive or judgmental.</p>
                
                <p><strong>Avoid Gossip:</strong> Do not spread rumors or engage in detraction (revealing true faults unnecessarily) or calumny (spreading false information about others).</p>
                
                <p><strong>Respect Clergy:</strong> While respectful questions and concerns are appropriate, maintain proper respect for priests, deacons, religious, and bishops.</p>
                
                <p><strong>Private Matters:</strong> Some discussions are better handled privately. Use direct messages for sensitive personal matters.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">Marketplace Conduct</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                When buying or selling in the Parish marketplace:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Be honest and accurate in your listings</li>
                <li>Price items fairly</li>
                <li>Respond promptly to inquiries</li>
                <li>Honor your commitments</li>
                <li>Meet safely in public places when exchanging items</li>
                <li>Report any suspicious or fraudulent activity</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                See our <Link to="/marketplace-terms" className="text-[var(--liturgical-primary)] hover:underline">Marketplace Terms</Link> for complete guidelines.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4 flex items-center gap-2">
                <Flag className="w-5 h-5 text-[var(--liturgical-primary)]" /> Reporting & Enforcement
              </h2>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">How to Report</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                If you see content that violates these guidelines, please report it using the report button (flag icon) on posts, listings, or profiles. You can also contact us directly at <a href="mailto:safety@parish.com" className="text-[var(--liturgical-primary)] hover:underline">safety@parish.com</a>.
              </p>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">Our Response</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                We review all reports and take appropriate action, which may include:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Warning the user</li>
                <li>Removing the content</li>
                <li>Temporary suspension</li>
                <li>Permanent account termination</li>
                <li>Reporting to law enforcement (in cases of illegal activity or imminent harm)</li>
              </ul>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">Appeals</h3>
              <p className="text-[#4A4A4A] leading-relaxed">
                If you believe your content was removed in error or your account was suspended unfairly, you may appeal by contacting <a href="mailto:appeals@parish.com" className="text-[var(--liturgical-primary)] hover:underline">appeals@parish.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">A Final Word</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                We are all members of the Body of Christ, called to build each other up in faith, hope, and love. Let Parish be a place where the light of Christ shines through our interactions, where newcomers feel welcomed, and where our online community reflects the beauty of our Catholic faith.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed mt-4 italic">
                "Let all that you do be done in love." — 1 Corinthians 16:14
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">Contact Us</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                Questions about these guidelines? Contact us at:
              </p>
              <div className="bg-[#F9F7F2] rounded-lg p-4 mt-4">
                <p className="text-[#4A4A4A]"><strong>Email:</strong> <a href="mailto:community@parish.com" className="text-[var(--liturgical-primary)] hover:underline">community@parish.com</a></p>
              </div>
            </section>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            See also: <Link to="/privacy" className="text-[var(--liturgical-primary)] hover:underline">Privacy Policy</Link> · <Link to="/terms" className="text-[var(--liturgical-primary)] hover:underline">Terms of Service</Link> · <Link to="/cookies" className="text-[var(--liturgical-primary)] hover:underline">Cookie Policy</Link> · <Link to="/marketplace-terms" className="text-[var(--liturgical-primary)] hover:underline">Marketplace Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
